/**
 * @file BackupDiskUsage.tsx
 * @description «Диск копий» (phase19.26, решение пользователя 2026-09-26):
 * полоска занятости диска локальной копии и сколько занимают сами копии.
 *
 * Замер присылает агент после запуска бэкапа, поэтому до первого запуска
 * данных нет. При `diskWarning` полоска и подпись — предупреждающего цвета.
 * Локальная копия есть всегда (и при внешнем хранилище), так что блок
 * показывается при любом типе назначения.
 */

import { HardDrive } from "lucide-react";

import type { BackupDiskStats } from "../../../lib/api-backups";
import { formatGb, usedPercent } from "../../../lib/format";
import { cn } from "@/lib/utils";

interface Props {
  stats: BackupDiskStats | null;
  warning: boolean;
}

export function BackupDiskUsage({ stats, warning }: Props) {
  if (!stats) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-surface-low px-3 py-2 text-xs text-on-surface-variant">
        <HardDrive className="size-4 shrink-0" aria-hidden />
        Диск копий: данные появятся после первого бэкапа.
      </div>
    );
  }

  const percent = usedPercent(stats.usedBytes, stats.totalBytes);
  const updated = stats.updatedAt
    ? new Date(stats.updatedAt).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" })
    : "—";

  return (
    <div className="flex flex-col gap-1.5 rounded-xl bg-surface-low px-3 py-2.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="flex items-center gap-2 text-sm font-medium text-on-surface">
          <HardDrive className="size-4 shrink-0 text-on-surface-variant" aria-hidden />
          Диск копий
          {stats.hostname ? (
            <span className="text-xs font-normal text-on-surface-variant">{stats.hostname}</span>
          ) : null}
        </span>
        <span className={cn("text-xs tabular-nums", warning ? "text-warning" : "text-on-surface-variant")}>
          занято {formatGb(stats.usedBytes)} из {formatGb(stats.totalBytes)}
        </span>
      </div>
      <div
        role="progressbar"
        aria-label="Занятость диска копий"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        className="h-2 w-full overflow-hidden rounded-full bg-on-surface/10"
      >
        <div
          className={cn("h-full rounded-full", warning ? "bg-warning" : "bg-primary")}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs text-on-surface-variant tabular-nums">
        Копии занимают {formatGb(stats.backupBytes)} · свободно {formatGb(stats.freeBytes)} · данные
        на {updated}
      </span>
      <span className="truncate font-mono text-xs text-on-surface-variant">{stats.path}</span>
    </div>
  );
}
